import classNames from 'classnames';
import React, { ReactNode } from 'react';
import Overlay from 'react-bootstrap/Overlay';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { MessageDescriptor, PrimitiveType } from 'react-intl';

import FormattedMessage from 'components/formatters/FormattedMessage';

import { FormatXMLElementFn } from 'intl-messageformat';
import './Hint.scss';

/**
 * Hint
 *
 * Shows a Bootstrap tooltip on hover/focus of its child.
 *
 * @param hint - Tooltip content (string or JSX), undefined indicates no hint should be rendered
 * @param width - Optional max width for the tooltip
 * @param animated - Optional boolean to enable entrance animation for the tooltip
 * @param animating - Optional boolean to enable continuous animation for the tooltip
 * @param placement - Tooltip position relative to the child:
 *   'top' | 'bottom' | 'left' | 'right' | 'auto' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end' | 'left-start' | 'left-end' | 'right-start' | 'right-end'
 *   - 'top', 'bottom', 'left', 'right': positions the tooltip above, below, left, or right of the child
 *   - 'auto': lets Bootstrap choose the best position
 *   - '-start'/'-end' variants: align the tooltip to the start/end of the child
 */

export type HintPlacement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'auto'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end'
  | 'left-start'
  | 'left-end'
  | 'right-start'
  | 'right-end';

export type HintProps = {
  hint?: string | MessageDescriptor | React.JSX.Element;
  values?: Record<string, PrimitiveType | FormatXMLElementFn<string, string>>;
  width?: number;
  placement?: HintPlacement;
  triggerRef?: React.RefObject<HTMLElement>;
  show?: boolean; // Optional: control tooltip visibility for Overlay
  adjusted?: boolean; // Optional: adjust placement for small icons (shift 4 px to the left)
  animated?: boolean; // Optional: enable entrance animation for the tooltip
  animating?: boolean; // Optional: enable continuous animation for the tooltip
  className?: string;
};

export function Hint(props: React.PropsWithChildren<HintProps>): ReactNode {
  const {
    hint,
    values,
    width,
    placement = 'top',
    children,
    triggerRef,
    show,
    adjusted,
    animated,
    animating,
    className,
  } = props;

  // Tooltip style
  const tooltipStyle: React.CSSProperties = {
    width: width ? 'max-content' : undefined,
    maxWidth: width,
    whiteSpace: 'normal',
  };

  let hintContent: React.JSX.Element | string | undefined;
  if (!hint) {
    return children;
  }
  if (typeof hint === 'string') {
    hintContent = hint;
  } else if (React.isValidElement(hint)) {
    hintContent = hint;
  } else if (typeof hint === 'object' && 'id' in hint && hint.id) {
    hintContent = (
      <FormattedMessage
        id={hint.id}
        defaultMessage={hint.defaultMessage}
        values={values}
      />
    );
  } else {
    hintContent = undefined;
  }

  if (!hintContent) {
    return children;
  }

  // If no child and triggerRef is provided, use Overlay
  if (!children && triggerRef?.current) {
    // Tooltip visibility can be controlled via the show prop, or always shown for debugging
    return (
      <Overlay target={triggerRef.current} show={show} placement={placement}>
        {(overlayProps) => (
          <Tooltip
            className={classNames('hint noselect', className, {
              hint_animated: animated,
              hint_animating: animating,
            })}
            {...overlayProps}
            style={tooltipStyle}
          >
            {hintContent}
          </Tooltip>
        )}
      </Overlay>
    );
  }

  // Default: use OverlayTrigger as a wrapper
  return (
    <OverlayTrigger
      placement={placement}
      overlay={
        <Tooltip
          className={classNames('hint noselect', className, {
            hint_adjusted:
              adjusted &&
              (placement.includes('top') || placement.includes('bottom')),
            hint_animated: animated,
            hint_animating: animating,
          })}
          style={tooltipStyle}
        >
          {hintContent}
        </Tooltip>
      }
      delay={{ show: 100, hide: 100 }}
      show={show}
    >
      {React.isValidElement(children) ? (
        React.Children.only(children)
      ) : (
        <span />
      )}
    </OverlayTrigger>
  );
}
